import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { TrendingUp, AlertTriangle, Brain, Lightbulb } from "lucide-react";
import { ChartContainer } from "@/components/ui/chart";
import { Area, AreaChart, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { supabase } from "@/integrations/supabase/client";

const iconMap: Record<string, any> = {
  trend: TrendingUp,
  anomaly: AlertTriangle,
  recommendation: Brain,
};

const InsightsSection = () => {
  const [insights, setInsights] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data } = await supabase
          .from("proactive_insights")
          .select("*")
          .eq("user_id", user.id)
          .eq("is_dismissed", false)
          .order("created_at", { ascending: false })
          .limit(5);
        setInsights(data || []);
      } catch (error) {
        console.error("Error fetching insights:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInsights();
  }, []);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </Card>
    );
  }

  if (insights.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="rounded-full bg-muted p-4 mb-4">
            <Lightbulb className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-1">No Insights Yet</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            Run your first analysis or workflow to see AI-powered insights here.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">AI Insights</h2>
      <div className="space-y-4">
        {insights.map((insight) => {
          const Icon = iconMap[insight.insight_type] || Brain;
          return (
            <div
              key={insight.id}
              className="flex items-start space-x-4 p-4 rounded-lg bg-background border"
            >
              <div className={`p-2 rounded-full ${
                insight.severity === "high"
                  ? "bg-red-100 text-red-600"
                  : insight.severity === "medium"
                  ? "bg-yellow-100 text-yellow-600"
                  : "bg-blue-100 text-blue-600"
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium">{insight.title}</h3>
                <p className="text-sm text-muted-foreground">{insight.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default InsightsSection;
